import React, { useEffect } from "react";

// Global loader for Instagram embeds
const loadInstagramEmbeds = () => {
  if (typeof window === "undefined") return;

  const processInstagram = () => {
    if (
      window.instgrm &&
      window.instgrm.Embeds &&
      typeof window.instgrm.Embeds.process === "function"
    ) {
      try {
        window.instgrm.Embeds.process();
      } catch (err) {
        console.warn("Instagram Embeds.process error:", err);
      }
      return true;
    }
    return false;
  };

  // If window.instgrm already exists, run process immediately
  if (processInstagram()) {
    setTimeout(processInstagram, 300);
    return;
  }

  // Ensure https://www.instagram.com/embed.js is in document
  let script = document.getElementById("instagram-embed-script");
  if (!script) {
    script = document.createElement("script");
    script.id = "instagram-embed-script";
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      processInstagram();
      setTimeout(processInstagram, 200);
      setTimeout(processInstagram, 600);
    };
    document.body.appendChild(script);
  } else {
    script.addEventListener("load", () => {
      processInstagram();
      setTimeout(processInstagram, 200);
    });
  }

  // Polling in case script is loading or evaluation finishes slightly later
  let attempts = 0;
  const poll = setInterval(() => {
    attempts++;
    if (processInstagram() || attempts >= 20) {
      clearInterval(poll);
    }
  }, 200);
};

// Global loader for Twitter embeds
const loadTwitterEmbeds = (container) => {
  if (typeof window === "undefined") return;

  const processTwitter = () => {
    if (
      window.twttr &&
      window.twttr.widgets &&
      typeof window.twttr.widgets.load === "function"
    ) {
      try {
        window.twttr.widgets.load(container);
      } catch (err) {
        console.warn("Twitter widgets.load error:", err);
      }
      return true;
    }
    return false;
  };

  if (processTwitter()) return;

  let script = document.getElementById("twitter-wjs");
  if (!script) {
    script = document.createElement("script");
    script.id = "twitter-wjs";
    script.src = "https://platform.twitter.com/widgets.js";
    script.async = true;
    script.charset = "utf-8";
    script.onload = () => {
      processTwitter();
    };
    document.body.appendChild(script);
  } else {
    script.addEventListener("load", processTwitter);
  }
};

const InjectScript = React.memo(({ script, type }) => {
  const divRef = React.useRef(null);

  useEffect(() => {
    // Check for valid ref and script before proceeding (avoid unnecessary work)
    if (!divRef.current || !script) return;

    const doc = document.createRange().createContextualFragment(script);

    // Clear existing content and append script fragment atomically
    divRef.current.textContent = ""; // Use textContent for script injection (security best practice)
    divRef.current.appendChild(doc);

    // 1. Process Instagram embeds
    const igElements = divRef.current.querySelectorAll(
      ".instagram-media, blockquote[data-instgrm-permalink]"
    );
    if (igElements.length > 0) {
      igElements.forEach((el) => {
        const link = el.querySelector("a");
        const permalink =
          el.getAttribute("data-instgrm-permalink") ||
          (link ? link.getAttribute("href") : "");

        // If link text is empty, provide a visible, clickable fallback card
        if (link && !link.textContent.trim() && permalink) {
          link.innerHTML = `
            <div class="instagram-fallback-card">
              <i class="fab fa-instagram"></i>
              <span>Instagram પર આ રીલ/પોસ્ટ જુઓ (View Reel on Instagram)</span>
            </div>
          `;
          link.style.textDecoration = "none";
          link.style.display = "block";
        }
      });

      // Remove redundant inline embed.js script to avoid duplicate or protocol errors
      const inlineIgScripts = divRef.current.querySelectorAll(
        'script[src*="instagram.com/embed.js"]'
      );
      inlineIgScripts.forEach((s) => s.remove());

      // Trigger official Instagram Embeds processor
      loadInstagramEmbeds();

      // Fallback: If after 3 seconds Instagram embed.js hasn't transformed it into an iframe,
      // load the direct Instagram embed iframe so user ALWAYS sees the reel!
      const fallbackTimer = setTimeout(() => {
        if (!divRef.current) return;
        const pendingIg = divRef.current.querySelectorAll(
          "blockquote.instagram-media:not(.instagram-media-rendered)"
        );
        pendingIg.forEach((el) => {
          if (el.querySelector("iframe")) return;
          const permalink =
            el.getAttribute("data-instgrm-permalink") ||
            el.querySelector("a")?.getAttribute("href");
          if (!permalink) return;

          const match = permalink.match(/\/(reel|p|tv)\/([A-Za-z0-9_-]+)/);
          if (match) {
            const embedType = match[1];
            const mediaId = match[2];
            const iframe = document.createElement("iframe");
            iframe.src = `https://www.instagram.com/${embedType}/${mediaId}/embed/`;
            iframe.className = "instagram-media instagram-media-rendered";
            iframe.setAttribute("frameborder", "0");
            iframe.setAttribute("scrolling", "no");
            iframe.setAttribute("allowtransparency", "true");
            iframe.style.width = "100%";
            iframe.style.maxWidth = "540px";
            iframe.style.minHeight = "480px";
            iframe.style.border = "1px solid #dbdbdb";
            iframe.style.borderRadius = "8px";
            iframe.style.margin = "16px auto";
            iframe.style.display = "block";

            el.innerHTML = "";
            el.appendChild(iframe);
            el.classList.add("instagram-media-rendered");
          }
        });
      }, 3000);

      return () => clearTimeout(fallbackTimer);
    }

    // 2. Process Twitter embeds
    const twElements = divRef.current.querySelectorAll(
      ".twitter-tweet, blockquote.twitter-tweet"
    );
    if (twElements.length > 0) {
      loadTwitterEmbeds(divRef.current);
    }
  }, [script, type]); // Dependency array for useEffect

  return <div ref={divRef} />;
});

export default InjectScript;